<div class="flex flex-col gap-sm">
  <h2 class="reset-h text-2xl font-bold text-default">{{ 'Who signs this contract?' | translate }}</h2>
  <p class="reset-p text-base text-subtle">
    @if (noDigitalSignature()) {
      {{ 'No digital signature fields are added.' | translate }}
    } @else {
      {{ 'Signing runs in this order.' | translate }}
      @if (!hasMissingParty()) {
        {{ 'We found the employer and employee fields in your document.' | translate }}
      }
    }
  </p>
</div>

@if (noDigitalSignature()) {
  <sb-notice variant="info" role="status">
    {{ 'The template only needs an area for the signatures. Everyone signs on paper.' | translate }}
  </sb-notice>
} @else {
  @if (hasMissingParty()) {
    <sb-notice variant="warning">
      <h3 sbNoticeHeader>{{ 'Not every signer was found' | translate }}</h3>
      <p class="reset-p">
        {{
          'Download the document, add the missing signature and date in Word, then upload it again. We check it again for you.'
            | translate
        }}
      </p>
      <div class="flex flex-wrap gap-sm">
        <button sb-button color="secondary" [disabled]="isDownloading()" (click)="download.emit()">
          {{ 'Download document' | translate }}
        </button>
        <button sb-button color="secondary" (click)="fixedFile.click()">
          {{ 'Upload fixed document' | translate }}
        </button>
        <input
          #fixedFile
          class="hidden"
          type="file"
          tabindex="-1"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          [attr.aria-label]="'Fixed document' | translate"
          (change)="chooseFile(fixedFile)"
        />
      </div>
    </sb-notice>

    @if (rejectedFile()) {
      <sb-notice variant="danger">{{ 'Only .docx files are supported.' | translate }}</sb-notice>
    }

    @if (downloadFailed()) {
      <sb-notice variant="danger">{{ 'The document couldn’t be downloaded. Try again.' | translate }}</sb-notice>
    }
  }

  <ol class="reset-ul flex flex-col gap-md">
    @for (signer of signers(); track signer.title; let order = $index) {
      <li>
        <sb-card>
          <sb-card-content>
            <div class="flex items-start gap-md">
              <span
                class="flex size-xl shrink-0 items-center justify-center rounded-full bg-brand-subtle text-base text-brand"
                aria-hidden="true"
              >
                {{ order + 1 }}
              </span>
              <div class="flex min-w-0 flex-col gap-2xs">
                <h3 class="reset-h flex items-baseline gap-sm text-lg font-semibold text-default">
                  {{ signer.title | translate }}
                  @if (signer.alwaysSigns) {
                    <span class="text-sm font-normal text-subtle">{{ 'Always signs' | translate }}</span>
                  }
                </h3>
                <p class="reset-p text-base text-subtle">{{ signer.subtitle | translate }}</p>
              </div>
            </div>

            <div sbCardDivider></div>

            <div class="flex">
              @if (signer.found) {
                <sb-badge color="green" size="medium">{{ 'Signature and date found' | translate }}</sb-badge>
              } @else if (signer.alwaysSigns) {
                <sb-badge color="yellow" size="medium">{{ 'Signature and date not found' | translate }}</sb-badge>
              } @else {
                <sb-badge color="brand" size="medium">{{ 'Fields are added when you save' | translate }}</sb-badge>
              }
            </div>
          </sb-card-content>
        </sb-card>
      </li>
    }
  </ol>

  @if (guardiansEnabled) {
    @if (guardianCountEnabled) {
      <sb-form-field
        [label]="'Number of guardians' | translate"
        [hint]="'For employees under 18. We place the fields and add them to the signing order.' | translate"
      >
        <sb-select
          [items]="guardianCountItems"
          [ngModel]="guardianCount()"
          (ngModelChange)="guardianCount.set($event)"
        ></sb-select>
      </sb-form-field>
    } @else {
      <sb-checkbox [truncateLabel]="false" [ngModel]="guardianCount() > 0" (ngModelChange)="setGuardianChecked($event)">
        {{ 'Requires guardian signature' | translate }}
      </sb-checkbox>
    }
  }
}

<sb-checkbox [truncateLabel]="false" [ngModel]="noDigitalSignature()" (ngModelChange)="noDigitalSignature.set($event)">
  {{ 'This contract is signed on paper' | translate }}
</sb-checkbox>
